import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { MANIFESTO, BRAND } from "../data/content";

const FOUNDER_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_radiant-skincare-15/artifacts/m4fc72v9_23BA626F-3C71-40CF-B84A-65D8A7B92FEF.jpeg";
const STATS = [
  { n: "1st", l: "Ingredients, before scent" },
  { n: "0", l: "Fillers, ever" },
  { n: "2", l: "Continents, rare butters" },
  { n: "100%", l: "Active nourishment" },
];

export default function About() {
  return (
    <div className="pt-28" data-testid="about-page">
      {/* intro */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-4">Our Story</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-moss max-w-4xl">
            Built for people done <span className="italic text-gold">guessing</span> what's on their skin.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-muted leading-relaxed">{BRAND.about}</p>
        </Reveal>
      </section>

      {/* image + promise */}
      <section className="grid lg:grid-cols-2">
        <div className="relative min-h-[440px] overflow-hidden">
          <motion.img
            initial={{ scale: 1.1 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
            src={FOUNDER_IMG}
            alt="Ruth's Glow body butter jar on a marble pedestal"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <div className="flex flex-col justify-center bg-cream-2 text-moss grain relative px-6 py-20 sm:px-16 border-y border-[#E4DDD0]">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-5">Our Promise</p>
            <h2 className="font-serif text-3xl sm:text-4xl leading-snug">"{BRAND.promise}"</h2>
            <p className="mt-6 max-w-md text-muted leading-relaxed">{BRAND.positioning}</p>
          </Reveal>
        </div>
      </section>

      {/* stats */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 grid grid-cols-2 gap-8 md:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.l} delay={i * 0.08}>
            <div className="border-t border-[#E4DDD0] pt-5">
              <p className="font-serif text-5xl text-gold">{s.n}</p>
              <p className="mt-2 font-mono text-xs uppercase tracking-wider text-muted">{s.l}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <Marquee />

      {/* manifesto chapters */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24" data-testid="about-manifesto">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-gold mb-4">The Manifesto</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-16 max-w-2xl leading-[1.05] text-moss">
            What we refuse to compromise.
          </h2>
        </Reveal>
        {MANIFESTO.map((c, i) => (
          <Reveal key={c.chapter} delay={i * 0.05}>
            <div className="grid gap-6 border-t border-[#E4DDD0] py-10 md:grid-cols-[auto_1fr_1.4fr] md:gap-12">
              <span className="font-serif text-6xl text-gold">{c.chapter}</span>
              <div>
                <h3 className="font-serif text-3xl text-moss">{c.title}</h3>
                <p className="mt-2 font-serif text-xl italic text-gold">{c.quote}</p>
              </div>
              <p className="text-muted leading-relaxed">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-cream-2 py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl text-moss">Ready to feed your skin?</h2>
            <Link to="/shop" className="btn-gold mt-8 inline-flex items-center gap-2 rounded-sm px-8 py-4 text-sm font-semibold uppercase tracking-wider" data-testid="about-shop-cta">
              Shop the butters <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
